import { useCurrentUser, useWorkspace } from 'components/providers'
import { isEqual } from 'lodash-es'
import { ReactNode, createContext, useContext, useEffect, useState } from 'react'
import { generatePath, useNavigate, useParams } from 'react-router'
import { IRegistrationConsent } from 'types'
import { deleteDoc, removeEmpty, setDoc, useBeforeUnload, useDoc } from 'utils'
import { useMutateRegistrationConsentRecords } from '../../utils/registrationConsentUtils'
import { RoutePath } from '../../utils/routing'

interface RegistrationConsentContextValue {
  consent: IRegistrationConsent | null | undefined
  isDirty: boolean
  updateConsent: (update: Partial<IRegistrationConsent>) => void
  saveConsent: () => Promise<void>
  deleteConsent: () => Promise<void>
  resetConsent: () => void
}

export const useRegistrationConsent = () => useContext(RegistrationConsentContext)

const RegistrationConsentContext = createContext<RegistrationConsentContextValue>(
  {} as RegistrationConsentContextValue
)

export function RegistrationConsentProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate()
  const { workspace } = useWorkspace()
  const { id } = useParams()
  const user = useCurrentUser()
  const { updateRegistrationConsentRecord, removeRegistrationConsentRecord } =
    useMutateRegistrationConsentRecords()
  const [consent] = useDoc('registrationConsents', id)
  const [draft, setDraft] = useState<IRegistrationConsent | null>()

  const isDirty = Boolean(draft) && !isEqual(draft, consent)
  useBeforeUnload(isDirty)

  useEffect(() => {
    setDraft(consent)
  }, [consent])

  function updateConsent(update: Partial<IRegistrationConsent>) {
    setDraft(s => (s ? removeEmpty({ ...s, ...update }) : null))
  }

  async function saveConsent() {
    if (draft && user) {
      const updated = await setDoc(
        user.id,
        'registrationConsents',
        removeEmpty(draft, { emptyStrings: true, emptyObjects: true, emptyArrays: true })
      )
      updateRegistrationConsentRecord(updated)
    }
  }

  async function deleteConsent() {
    if (consent && user && workspace) {
      await deleteDoc('registrationConsents', consent.id)
      removeRegistrationConsentRecord(consent.id)
      navigate(generatePath(RoutePath.RegistrationConsents, { workspaceId: workspace.id }))
    }
  }

  function resetConsent() {
    setDraft(consent)
  }

  return (
    <RegistrationConsentContext.Provider
      value={{
        consent: draft,
        isDirty,
        updateConsent,
        saveConsent,
        deleteConsent,
        resetConsent,
      }}
    >
      {children}
    </RegistrationConsentContext.Provider>
  )
}
